// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import {
  CheckCircleOutlined,
  DeviceUnknownOutlined,
  InfoOutlined,
  JoinRightOutlined,
  LowPriorityOutlined,
  RuleOutlined,
  ScreenLockPortraitOutlined,
  SearchOffOutlined,
} from '@/constants/hardfin-standard-icons';
import {
  QuarantineResolutionKind,
  QuarantinedSerial,
  Shipment,
} from '@/shipment/api/types';
import { QuarantineResolutionActions } from '@/shipment/components/quarantine-resolution-actions';
import { shipmentClaimedSerials } from '@/shipment/utilities/shipment-claimed-serials';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, ReactElement } from 'react';

interface Props {
  shipment: Shipment;
  isInbound: boolean;
}

// Spacing units, so the relative time reads on one line with room to spare.
const REPORTED_COLUMN_WIDTH_UNITS = 16;

const RESOLUTION_EFFECT =
  'Resolving a quarantined line item will cause the held asset to be processed immediately. Any other line items on the shipment that had valid data were not held and have already been processed.';

const RESOLUTION_DESCRIPTIONS: Record<QuarantineResolutionKind, string> = {
  KEPT_HARDFIN_ITEM: 'Kept Hardfin item',
  CORRECTED_SERIAL: 'Corrected serial',
  CREATED_ASSET: 'Created asset',
  ACCEPTED_SERIAL: 'Accepted serial',
};

// Why the serial is held, keyed on its quarantine type.
const QUARANTINE_REASONS: Record<
  QuarantinedSerial['quarantineType'],
  { icon: ReactElement; label: string }
> = {
  SKU_TRANSMUTATION: {
    icon: <JoinRightOutlined fontSize="small" color="action" />,
    label: 'Mismatched SKU',
  },
  UNKNOWN_SERIAL: {
    icon: <DeviceUnknownOutlined fontSize="small" color="action" />,
    label: 'Unrecognized serial',
  },
  SERIAL_PATTERN_MISMATCH: {
    icon: <RuleOutlined fontSize="small" color="action" />,
    label: 'Unexpected serial format',
  },
  DUPLICATE_IN_ORDER: {
    icon: <LowPriorityOutlined fontSize="small" color="action" />,
    label: 'Duplicate serial on the shipment',
  },
  DUPLICATE_ALREADY_EXISTS: {
    icon: <ScreenLockPortraitOutlined fontSize="small" color="action" />,
    label: 'Hardfin already has this serial',
  },
  MISSING_SERIAL: {
    icon: <SearchOffOutlined fontSize="small" color="action" />,
    label: 'Missing serial',
  },
};

function itemLabel(item: { sku: string; name: string }): string {
  return `${item.sku} (${item.name})`;
}

/** The resolved-row tooltip: who resolved the hold and what it settled on. */
function ResolutionTooltip(props: {
  held: QuarantinedSerial;
  resolution: NonNullable<QuarantinedSerial['resolution']>;
}) {
  const { held, resolution } = props;

  const rows: { label: string; value: string }[] = [
    {
      label: 'Resolved by',
      value: `${resolution.resolvedBy.firstName} ${resolution.resolvedBy.lastName}`,
    },
  ];
  if (resolution.kind === 'CORRECTED_SERIAL') {
    rows.push(
      { label: 'Item', value: itemLabel(held.reportedItem) },
      { label: 'Original 3PL serial', value: held.serial },
      {
        label: 'Corrected Hardfin serial',
        value: resolution.resolvedAsset?.serial ?? '—',
      }
    );
  } else if (
    resolution.kind === 'CREATED_ASSET' ||
    resolution.kind === 'ACCEPTED_SERIAL'
  ) {
    rows.push(
      { label: 'Item', value: itemLabel(held.reportedItem) },
      { label: 'Serial', value: held.serial }
    );
  } else {
    rows.push(
      { label: 'Original 3PL item', value: itemLabel(held.reportedItem) },
      {
        label: 'Corrected Hardfin item',
        value: held.existingItem ? itemLabel(held.existingItem) : '—',
      },
      { label: 'Serial', value: held.serial }
    );
  }
  rows.push({ label: 'Note', value: resolution.note });

  return (
    <>
      {rows.map((row, index) => (
        <Fragment key={row.label}>
          {index > 0 ? <br /> : null}
          {row.label}: {row.value}
        </Fragment>
      ))}
    </>
  );
}

/**
 * Orders holds by serial, so every row for one serial reads together whether or
 * not it is resolved.
 */
function sortHoldsBySerial(serials: QuarantinedSerial[]): QuarantinedSerial[] {
  return [...serials].sort((a, b) => {
    const serialOrder = a.serial.localeCompare(b.serial);
    if (serialOrder !== 0) {
      return serialOrder;
    }
    // Repeats of one serial read oldest first. Saying so beats leaning on a
    // stable sort to preserve an order the server never promised.
    const reportedOrder = a.reportedAt.toMillis() - b.reportedAt.toMillis();
    return reportedOrder === 0 ? a.key.localeCompare(b.key) : reportedOrder;
  });
}

/** The shipment's home for resolving held serials. */
export function QuarantinePanel(props: Props) {
  const holds = sortHoldsBySerial(props.shipment.quarantinedSerials);
  const shipmentSerials = shipmentClaimedSerials(props.shipment);
  const openCount = holds.filter((held) => held.resolution === null).length;

  if (holds.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No serials on this shipment are in quarantine
      </Typography>
    );
  }

  return (
    <Stack spacing={2} paddingY={2}>
      {openCount > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          display="flex"
          alignItems="center"
          gap={0.5}
        >
          The 3PL data for this shipment has invalid entries, so some assets
          cannot proceed. Problematic line items are quarantined here until you
          resolve the issues
          <Tooltip arrow title={RESOLUTION_EFFECT}>
            <InfoOutlined fontSize="inherit" />
          </Tooltip>
        </Typography>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reason</TableCell>
              <TableCell>Serial</TableCell>
              <TableCell>3PL reported item</TableCell>
              <TableCell>Hardfin item record</TableCell>
              <TableCell
                sx={{
                  width: (theme) => theme.spacing(REPORTED_COLUMN_WIDTH_UNITS),
                }}
              >
                Reported
              </TableCell>
              <TableCell>Resolution</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holds.map((held) => (
              <TableRow key={held.key}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {QUARANTINE_REASONS[held.quarantineType].icon}
                    <Typography variant="body2">
                      {QUARANTINE_REASONS[held.quarantineType].label}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  {held.serial === '' ? (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {held.existingAsset ? (
                        <Link
                          to="/assets/$assetKey"
                          params={{ assetKey: held.existingAsset.key }}
                        >
                          {held.serial}
                        </Link>
                      ) : (
                        held.serial
                      )}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {held.reportedItem.sku}{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      ({held.reportedItem.name})
                    </Typography>
                  </Typography>
                </TableCell>
                <TableCell>
                  {held.existingItem ? (
                    <Typography variant="body2">
                      {held.existingItem.sku}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        ({held.existingItem.name})
                      </Typography>
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip
                    arrow
                    title={held.reportedAt.toLocaleString({
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  >
                    <Typography variant="body2" whiteSpace="nowrap">
                      {held.reportedAt.toRelative()}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {held.resolution === null ? (
                    <Box display="flex" justifyContent="flex-start">
                      <QuarantineResolutionActions
                        held={held}
                        shipmentKey={props.shipment.key}
                        isInbound={props.isInbound}
                        originLocation={props.shipment.fromLocation}
                        receiveLocation={props.shipment.toLocation}
                        inventoryDate={
                          props.isInbound
                            ? props.shipment.receivedAt
                            : props.shipment.shippedAt
                        }
                        shipmentSerials={shipmentSerials}
                      />
                    </Box>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-start"
                      alignItems="center"
                    >
                      <CheckCircleOutlined color="success" fontSize="small" />
                      <Tooltip
                        arrow
                        describeChild
                        title={
                          <ResolutionTooltip
                            held={held}
                            resolution={held.resolution}
                          />
                        }
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: 'underline dotted',
                            textUnderlineOffset: '3px',
                          }}
                        >
                          {RESOLUTION_DESCRIPTIONS[held.resolution.kind]}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
